import React, { useEffect, useMemo, useRef, useState } from "react";
import useLockInSession from "./useLockInSession";
import { createAmbient, SOUNDS, DEFAULT_SOUND } from "./ambient";
import { DURATIONS, DEFAULT_MINUTES, progressFraction, formatClock, startLabel } from "./session";
import "../ui/tokens.css";
import "./lockin.css";

const RING = 120;
const CIRC = 2 * Math.PI * RING;

const MUSIC_LINKS = [
  { label: "Spotify", href: "https://open.spotify.com/" },
  { label: "Apple Music", href: "https://music.apple.com/" },
  { label: "YouTube Music", href: "https://music.youtube.com/" },
];

// Lock-In: a focus session. Setup (length, task, sound) -> active (countdown) -> finished.
export default function LockInScreen({ tasks = [], onBack, onCompleteTask, now, createAmbientImpl = createAmbient }) {
  const open = useMemo(() => tasks.filter((t) => !t.completed), [tasks]);
  const { phase, session, remaining, start, stop, again } = useLockInSession(now ? { now } : undefined);
  const [minutes, setMinutes] = useState(DEFAULT_MINUTES);
  const [taskId, setTaskId] = useState("");
  const [sound, setSound] = useState(DEFAULT_SOUND);
  const [marked, setMarked] = useState(false);
  const ambientRef = useRef(null);
  const wakeRef = useRef(null);

  if (!ambientRef.current) ambientRef.current = createAmbientImpl();

  const releaseWake = () => {
    if (wakeRef.current) {
      wakeRef.current.release().catch(() => {});
      wakeRef.current = null;
    }
  };

  // Sound and screen-awake follow the phase. Starting audio must happen inside the tap that began the
  // session, so the Start handler calls play() itself; this effect only handles stopping and the chime.
  useEffect(() => {
    if (phase === "active") {
      if (navigator.wakeLock && navigator.wakeLock.request) {
        navigator.wakeLock.request("screen").then((l) => { wakeRef.current = l; }).catch(() => {});
      }
    } else {
      releaseWake();
    }
    if (phase === "finished") {
      ambientRef.current.stop();
      ambientRef.current.chime();
    }
    if (phase === "setup") ambientRef.current.stop();
  }, [phase]);

  useEffect(() => {
    const ambient = ambientRef.current;
    return () => {
      ambient.close();
      releaseWake();
    };
  }, []);

  const chosenTask = open.find((t) => String(t.id) === String(taskId)) || null;

  const begin = () => {
    setMarked(false);
    ambientRef.current.play(sound);
    start({ minutes, task: chosenTask ? { id: chosenTask.id, text: chosenTask.text } : null, sound });
  };

  const anotherRound = () => {
    setMarked(false);
    ambientRef.current.play(session ? session.sound : "off");
    again();
  };

  const changeSoundLive = (id) => {
    setSound(id);
    if (phase === "active") ambientRef.current.play(id);
  };

  const soundPicker = (
    <div className="li-chips" role="radiogroup" aria-label="Sound">
      {SOUNDS.map((s) => (
        <button
          key={s.id}
          type="button"
          role="radio"
          aria-checked={sound === s.id}
          className={`li-chip${sound === s.id ? " is-on" : ""}`}
          onClick={() => changeSoundLive(s.id)}
        >
          {s.label}
        </button>
      ))}
    </div>
  );

  let body;
  if (phase === "setup") {
    body = (
      <div className="li-col">
        <h2 className="li-head">Time to focus</h2>
        <p className="li-sub">Pick a length. Rigby will keep the time while you work.</p>

        <div className="li-label" id="li-len">Length</div>
        <div className="li-chips" role="radiogroup" aria-labelledby="li-len">
          {DURATIONS.map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={minutes === m}
              className={`li-chip${minutes === m ? " is-on" : ""}`}
              onClick={() => setMinutes(m)}
            >
              {m} min
            </button>
          ))}
        </div>

        <label className="li-label" htmlFor="li-task">What are you working on?</label>
        <select id="li-task" className="li-select" value={taskId} onChange={(e) => setTaskId(e.target.value)}>
          <option value="">Nothing in particular</option>
          {open.map((t) => (
            <option key={t.id} value={t.id}>{t.text.length > 60 ? `${t.text.slice(0, 57)}...` : t.text}</option>
          ))}
        </select>

        <div className="li-label">Sound</div>
        {soundPicker}
        <p className="li-fine">
          Prefer your own music? Open{" "}
          {MUSIC_LINKS.map((l, i) => (
            <React.Fragment key={l.label}>
              {i > 0 ? (i === MUSIC_LINKS.length - 1 ? ", or " : ", ") : ""}
              <a href={l.href} target="_blank" rel="noopener noreferrer">{l.label}</a>
            </React.Fragment>
          ))}{" "}
          and set the sound to Off.
        </p>
        <p className="li-fine">
          Lofi music by noel.wav and ZHRØ, shared as CC0 on{" "}
          <a href="https://freesound.org/" target="_blank" rel="noopener noreferrer">Freesound</a>.
        </p>

        <button type="button" className="li-primary" onClick={begin}>{startLabel(minutes)}</button>
      </div>
    );
  } else if (phase === "active") {
    const frac = progressFraction(session.endAt, session.minutes, session.endAt - remaining * 1000);
    body = (
      <div className="li-col li-center">
        <div className="li-ring" role="timer" aria-label={`${formatClock(remaining)} left`}>
          <svg viewBox="0 0 260 260" aria-hidden="true">
            <circle className="li-ring-track" cx="130" cy="130" r={RING} />
            <circle
              className="li-ring-fill"
              cx="130"
              cy="130"
              r={RING}
              strokeDasharray={CIRC}
              strokeDashoffset={CIRC * frac}
              transform="rotate(-90 130 130)"
            />
          </svg>
          <div className="li-clock">{formatClock(remaining)}</div>
        </div>
        {session.task ? <p className="li-task">{session.task.text}</p> : <p className="li-task li-task--quiet">Focus session</p>}
        {soundPicker}
        <button type="button" className="li-ghost" onClick={stop}>End session</button>
      </div>
    );
  } else {
    body = (
      <div className="li-col li-center">
        <h2 className="li-head">Nice work</h2>
        <p className="li-sub">You focused for {session.minutes} minutes.</p>
        {session.task ? (
          <div className="li-done-task">
            <p className="li-task">{session.task.text}</p>
            {marked ? (
              <span className="li-marked">Marked done</span>
            ) : (
              <button
                type="button"
                className="li-secondary"
                onClick={() => {
                  onCompleteTask && onCompleteTask(session.task.id);
                  setMarked(true);
                }}
              >
                Mark this step done
              </button>
            )}
          </div>
        ) : null}
        <p className="li-fine">Stretch, sip some water, rest your eyes for a minute.</p>
        <button type="button" className="li-primary" onClick={anotherRound}>Another round</button>
        <button type="button" className="li-ghost" onClick={onBack}>Back to chat</button>
      </div>
    );
  }

  return (
    <div className={`li li--${phase}`}>
      <header className="li-bar">
        <button type="button" className="li-back" onClick={onBack}>Chat</button>
        <h1 className="li-title">Lock-In</h1>
        <span className="li-bar-space" />
      </header>
      <div className="li-scroll">{body}</div>
    </div>
  );
}
